import { Injectable } from '@angular/core';
import { LoggerService } from './logger.service';
import { findEmailContent } from '../campaign/campaign-editor/optional-setting/sms-delivery.mapper';

export interface EmailData {
  // Basic Info
  subject: string;
  fromEmail: string;
  
  // Header
  logo: File | string | null;
  logoUrl?: string;
  
  // Body
  image: File | string | null;
  imageUrl?: string;
  header: string;
  bodyText: string;
  body2Text: string;
  body3Text: string;
  body4Text: string;
  
  // Button
  buttonText: string;
  buttonPosition: 'top' | 'bottom';
  buttonColor: string;
  
  // Footer
  carWashName: string;
  street: string;
  city: string;
  state: string;
  zipCode: string;
  email: string;
  phoneNumber: string;
}

/** Check if sample data or campaign email content has meaningful fields. Shared by email component and preview. */
export function hasEmailContent(
  sampleData: Partial<EmailData> | null | undefined,
  campaignData: any
): boolean {
  if (
    sampleData &&
    Boolean(
      sampleData.subject ||
        sampleData.header ||
        sampleData.bodyText ||
        sampleData.buttonText ||
        sampleData.carWashName
    )
  ) {
    return true;
  }
  if (campaignData?.contents) {
    const emailContent = findEmailContent(campaignData.contents);
    if (emailContent) {
      return Boolean(
        emailContent.email_subject?.trim() ||
          emailContent.email_header?.trim() ||
          emailContent.email_body_text?.trim() ||
          emailContent.email_button_text?.trim() ||
          emailContent.email_footer_name?.trim()
      );
    }
  }
  return false;
}

export const DEFAULT_EMAIL_DATA: EmailData = {
  subject: '',
  fromEmail: '',
  logo: null,
  image: null,
  header: '',
  bodyText: '',
  body2Text: '',
  body3Text: '',
  body4Text: '',
  buttonText: '',
  buttonPosition: 'bottom',
  buttonColor: '#326FD1',
  carWashName: '',
  street: '',
  city: '',
  state: '',
  zipCode: '',
  email: '',
  phoneNumber: ''
};

/**
* Manages temporary storage and retrieval of email preview data across browser tabs using sessionStorage.
* @service
* @providedIn root
* @example
*   // Inject and use in a component or another service
*   constructor(private emailPreviewService: EmailPreviewService) {}
*   this.emailPreviewService.setEmailData(emailData);
*   const preview = this.emailPreviewService.getEmailData();
* @input EmailData data - The email preview model to store. May include File objects (logo/image); File objects are converted to object URLs and removed prior to serialization.
* @output EmailData|null getEmailData - Returns the previously stored email preview data (with logoUrl/imageUrl if available) or null if none found or on error.
* @method setEmailData(data: EmailData): void - Serializes the provided EmailData for sessionStorage, converts File objects to object URLs when necessary, strips raw File objects (since they can't be serialized), and saves the JSON under a private storage key. Errors during storage are caught and logged to the console.
* @method getEmailData(): EmailData|null - Reads and parses the stored JSON from sessionStorage and returns it. Returns null if nothing is stored or if an error/parse failure occurs; errors are logged to the console.
* @method clearEmailData(): void - Removes the stored email preview data from sessionStorage; errors during removal are caught and logged.
* @remarks
*   - Uses a private STORAGE_KEY ('email_preview_data') to namespace stored data.
*   - Designed for short-lived, cross-tab preview sharing; does not persist raw File objects.
*   - No Angular lifecycle hooks are used.
*/
@Injectable({
  providedIn: 'root'
})
export class EmailPreviewService {
  private readonly STORAGE_KEY = 'email_preview_data';

  constructor(private logger: LoggerService) {}

  /**
  * Stores email preview data in sessionStorage, converting File objects for logo/image to object URLs so the data can be serialized.
  * @example
  * etEmailData({ logo: new File([], 'logo.png'), image: new File([], 'image.png'), logoUrl: undefined, imageUrl: undefined })
  * undefined
  * @param {EmailData} data - Email preview data that may include File objects for logo and image and optional logoUrl/imageUrl.
  * @returns {void} Does not return a value.
  **/
  setEmailData(data: EmailData): void {
    // Convert File objects to URLs if needed for serialization
    const serializableData: any = { ...data };
    
    // If logo is a File, we'll use logoUrl if available, otherwise create object URL
    if (data.logo instanceof File && !data.logoUrl) {
      serializableData.logoUrl = URL.createObjectURL(data.logo);
    }
    
    if (data.image instanceof File && !data.imageUrl) {
      serializableData.imageUrl = URL.createObjectURL(data.image);
    }
    
    // Remove File objects as they can't be serialized
    serializableData.logo = null;
    serializableData.image = null;
    
    // Store in sessionStorage for cross-tab access
    try {
      sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(serializableData));
    } catch (error) {
      this.logger.error('Error storing email preview data:', error);
    }
  }

  getEmailData(): EmailData | null {
    try {
      const stored = sessionStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      this.logger.error('Error retrieving email preview data:', error);
    }
    return null;
  }

  clearEmailData(): void {
    try {
      sessionStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      this.logger.error('Error clearing email preview data:', error);
    }
  }
}

